import { isTrackedClass, ITrackedClass } from '@kb3d/models/interfaces';
import { IgnoreChanges } from './decorators/change-tracker';
import { Enumerable } from './decorators/enumerable';
import { BaseKb3dModel } from './decorators/kb3d-decorator';
import { instanceOf, instanceOfAny } from './decorators/model-utils';
import { Kb3dManager } from './kb3d-manager';
import { IKb3dObject } from './kb3d-models';
import { SceneNode } from './scene-node';
import { Token } from './token';
import { UUID } from './utilities';

export enum eNodeError {
    'FEATURE_REFERENCE',
    'GEOMETRY_SAVE',
}

export interface INodeError {
    error: eNodeError;
}

export type ValidationObject<T> = {
    [K in keyof T]: string[];
};

/**
 * Base class for kb3d models.  Any model that has an id and $type should derive from this class
 */
@BaseKb3dModel({
    token: Token.Kb3dObject,
})
export abstract class Kb3dObject implements IKb3dObject {
    constructor() {
        this.id = UUID.generate();
        this._dynamicId = UUID.generate();
    }

    public id: string;

    /**
     * Dynamically created id that is used to track assets in the 3d engine.  Ensures there is
     * no duplicate id's when there are nested scenes.
     */
    public _dynamicId: string;
    public comment?: string = "";

    @IgnoreChanges
    public name: string;

    @IgnoreChanges
    public $type: keyof typeof Token;

    get _token() {
        return Token.withKey(this.$type);
    }

    @IgnoreChanges
    public _manager: Kb3dManager | undefined;

    /**
     * The parent kb3dObject. This should not need to be set manually as adding nodes to arrays will set this automatically.
     * If this is a nested sceneNode, then _parent will be it's parent scene node
     * */
    public _parent: Kb3dObject | undefined;

    /** the closest ancestor this is a sceneNode.  If this is a nested scene node, then _parentScene will be equal to itself */
    @IgnoreChanges
    public _parentScene: SceneNode | undefined;

    @IgnoreChanges
    private __rootScene: SceneNode | undefined;
    get _rootScene() {
        return this.__rootScene;
    }

    protected setRootScene(scene: SceneNode | undefined) {
        if (this.__rootScene !== scene) {
            this.__rootScene = scene;
            if (isTrackedClass(this)) {
                (this as ITrackedClass).trackChanges(!!scene);
            }
        }
    }

    @IgnoreChanges
    public _level = -1;

    @IgnoreChanges
    public _errors: INodeError[] = [];

    _selected = false;

    @IgnoreChanges
    _expanded = false;

    private _metadata: any;
    @Enumerable()
    public get metadata(): any {
        if (!this._metadata) this._metadata = {};
        return this._metadata;
    }
    public set metadata(val) {
        this._metadata = val;
    }

    public clone<T extends Kb3dObject>(this: T) {
        if (this._manager) {
            return this._manager.clone(this);
        }
    }

    public serialize<T extends Kb3dObject>(this: T): Partial<T> | undefined {
        if (this._manager) {
            return this._manager.serialize(this);
        }
    }

    public serializeToJson(prettyPrint = false): string {
        if (this._manager) {
            return this._manager.serializeToJson(this, { prettyPrint });
        }
        return '';
    }

    public calculateParents(deletion = false, parent?: Kb3dObject) {
        // When a node is deleted and recreated, the children won't have parents because they were not re-added to the parent's arrays.
        // This solves that case by making sure the hierarchy is consistent from the top down
        if (parent && this._parent !== parent) {
            this._parent = parent;
        }
        if (this._parent) {
            this._level = this._parent._level == -1 ? -1 : this._parent._level + 1;
            this._parentScene = this._parent._parentScene;
            this.setRootScene(this._parent._rootScene);
            if (instanceOf(this, Token.SceneNode)) {
                //it's a nested scene
                this._parentScene = this;
            } else if(this._parent._manager) {
                this._manager = this._parent._manager; // a nested scene uses it's own manager, but everything else should have the same as it's parent
            }
            if (this._parentScene && !deletion) {
                this._manager?.register(this);
            }
        } else if (instanceOf(this, Token.SceneNode) && !deletion) {
            this._level = 0;
            this._parentScene = this;
            this.setRootScene(this);
            this._manager?.register(this);
        } else {
            this._level = -1; // -1 means this is not part of a scene node hierarchy

            // TODO add this change after release on Jan 13 2023, test to ensure it works
            // if(isTrackedClass(this)) {
            //     this.trackChanges(false);
            // }
        }

        for (const c of this.getChildren()) {
            c.calculateParents(deletion, this);
        }
    }

    /**
     * Derived classes that have children should override this
     */
    public getChildren(): Kb3dObject[] {
        return [];
    }

    /**
     * Derived classes with children should override this
     */
    public getChildArrayForType(token: Token): Kb3dObject[] | undefined {
        return undefined;
    }

    public getChildArray(child: Kb3dObject) {
        return this.getChildArrayForType(Token.withKey(child.$type));
    }

    public addChildren(...children: Kb3dObject[]) {
        for (const c of children) {
            this.getChildArray(c)?.push(c);
        }
    }

    public removeChildren(...children: Kb3dObject[]) {
        for (const c of children) {
            this.getChildArray(c)?.remove(c);
        }
    }

    public insertChildAfter(afterChild: Kb3dObject, newChild: Kb3dObject) {
        const arr1 = this.getChildArray(afterChild);
        const arr2 = this.getChildArray(newChild);
        if (!arr1 || !arr2) throw 'incompatible child';
        if (arr1 !== arr2) throw 'children mismatch';
        const index = arr1.indexOf(afterChild);
        if (index === -1) throw 'could not find given child';
        arr1.splice(index + 1, 0, newChild);
    }

    public getAncestors() {
        const ancestors = new Array<Kb3dObject>();
        let parent = this._parent;
        while (parent != null) {
            ancestors.push(parent);
            parent = parent._parent;
        }
        return ancestors;
    }

    public getNamePath() {
        let path = '';
        let self: Kb3dObject | undefined = this;
        while (self && !instanceOf(self, Token.SceneNode)) {
            path = self.name + (path ? '/' + path : '');
            self = self._parent;
        }
        return path;
    }

    /**
     * Filters children recursively based on a condition.  This is an O(n) operation, so only use when performance is not paramount
     * @param predicate expression to test whether the object should be included
     * @param includeSelf whether to include this kb3dObject in the search
     * @param alwaysIncludeChildren if true, will always search children even if the parent resolves to false
     */
    public filter<T extends Kb3dObject>(
        predicate: (node: Kb3dObject) => boolean,
        includeSelf = false,
        alwaysIncludeChildren = true
    ): T[] {
        const res: Kb3dObject[] = [];
        let included = true;
        if (includeSelf && predicate(this)) {
            res.push(this);
        } else {
            included = false;
        }
        if (included || alwaysIncludeChildren) {
            const children = this.getChildren();
            for (const child of children) {
                res.push(...child.filter(predicate, true, alwaysIncludeChildren));
            }
        }

        return res as T[];
    }

    /**
     * filters children by type given a list of type tokens
     * @param tokens
     */
    public filterByType<T extends Kb3dObject>(...tokens: Array<Token<T>>): T[] {
        return this.filter(node => instanceOfAny<T>(node, ...tokens)) as T[];
    }

    /**
     * filters children, but only those that qualify as nodes
     * @param predicate
     * @param includeSelf
     * @param alwaysIncludeChildren
     */
    public filterNodes<T extends Kb3dObject>(
        predicate: (node: Kb3dObject) => boolean,
        includeSelf = false,
        alwaysIncludeChildren = true
    ): T[] {
        const nodePredicate = (node: Kb3dObject) => node.isNode() && predicate(node);
        return this.filter(nodePredicate, includeSelf, alwaysIncludeChildren);
    }

    public isNode() {
        return this._token.isNode();
    }
}
